import { useState, useEffect } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { Check, School, Loader2, CheckCircle, XCircle, Gift, ChevronDown, ChevronUp, Sparkles } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { toast } from "sonner";
import AuthModal from "../components/AuthModal";
import { useContent, mergePlan } from "../context/ContentContext";
import { STUDENT_PLANS, TEACHER_PLANS } from "../lib/plans";

const API = "/api";

const TEACHER_TIERS = ["school", "teacher_plus", "teacher_pro"];

export default function Subscription({ user, setUser, authChecked }) {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [audience, setAudience] = useState(searchParams.get("tab") === "teacher" ? "teacher" : "student");
  const [loading, setLoading] = useState(null);
  const [redirecting, setRedirecting] = useState(false);
  const [subscription, setSubscription] = useState(null);
  const [checkingStatus, setCheckingStatus] = useState(false);
  const [showAuthModal, setShowAuthModal] = useState(false);
  const { content } = useContent();
  // Admin-edited plan copy (name, bullets, "Most Popular") merged over the built-in defaults
  const plans = (group) => Object.fromEntries(Object.entries(group).map(([k, p]) => [k, mergePlan(k, p, content)]));
  const ALL_PLANS = { ...plans(STUDENT_PLANS), ...plans(TEACHER_PLANS) };
  const [referralCode, setReferralCode] = useState("");
  const [showReferralInput, setShowReferralInput] = useState(false);
  // Live validation of the code: idle | checking | valid | invalid
  const [referralCheck, setReferralCheck] = useState({ status: "idle" });

  useEffect(() => {
    const code = referralCode.trim();
    if (!/^REF-[A-Z0-9]{6}$/.test(code)) {
      setReferralCheck({ status: "idle" });
      return;
    }
    setReferralCheck({ status: "checking" });
    const controller = new AbortController();
    const timer = setTimeout(async () => {
      try {
        const res = await fetch(`${API}/subscription/referral/check?code=${encodeURIComponent(code)}`, { credentials: "include", signal: controller.signal });
        const data = res.ok ? await res.json() : { valid: false };
        setReferralCheck(data.valid ? { status: "valid", referrerName: data.referrer_name, percent: data.discount_percent } : { status: "invalid" });
      } catch (e) {
        if (e.name !== "AbortError") setReferralCheck({ status: "idle" });
      }
    }, 350);
    return () => { clearTimeout(timer); controller.abort(); };
  }, [referralCode, user]);

  const referralValid = referralCheck.status === "valid";

  // Check for referral code in URL
  useEffect(() => {
    const ref = searchParams.get("ref");
    if (ref) {
      setReferralCode(ref.toUpperCase());
      setShowReferralInput(true);
    }
  }, [searchParams]);

  // Check for success redirect
  useEffect(() => {
    const sessionId = searchParams.get("session_id");
    if (sessionId) {
      checkPaymentStatus(sessionId);
    }
  }, [searchParams]);

  // Fetch current subscription
  useEffect(() => {
    if (user) {
      fetchSubscription();
    }
  }, [user]);

  const fetchSubscription = async () => {
    try {
      const response = await fetch(`${API}/subscription/my-subscription`, {
        credentials: "include"
      });
      if (response.ok) {
        const data = await response.json();
        setSubscription(data);
        // Teachers land on the Teacher tab by default (unless explicitly linked)
        if (!searchParams.get("tab") && TEACHER_TIERS.includes(data.subscription_tier)) {
          setAudience("teacher");
        }
      }
    } catch (error) {
      console.error("Error fetching subscription:", error);
    }
  };

  const checkPaymentStatus = async (sessionId) => {
    setCheckingStatus(true);
    try {
      const response = await fetch(`${API}/subscription/status/${sessionId}`, {
        credentials: "include"
      });
      
      if (response.ok) {
        const data = await response.json();
        if (data.payment_status === "paid") {
          const planName = ALL_PLANS[data.plan]?.name || "your new plan";
          toast.success(`Subscription activated! Welcome to ${planName}!`);
          fetchSubscription();
          // Update URL to remove session_id
          navigate("/subscription", { replace: true });
        }
      }
    } catch (error) {
      console.error("Error checking payment status:", error);
    } finally {
      setCheckingStatus(false);
    }
  };

  const handleSubscribe = async (plan) => {
    if (!user) {
      // Anonymous visitor: open the auth modal instead of a dead-end toast
      setShowAuthModal(true);
      return;
    }

    if (plan === "school") {
      navigate("/school-apply");
      return;
    }

    setLoading(plan);
    try {
      const response = await fetch(`${API}/subscription/checkout`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          plan: plan,
          origin_url: window.location.origin,
          quantity: 1,
          referral_code: plan === "student" ? (referralCode.trim() || null) : null
        })
      });

      if (response.ok) {
        const data = await response.json();
        // Show redirecting state before navigating
        setRedirecting(true);
        if (data.referral_applied) {
          toast.success(data.discount_percent ? `Referral applied - ${data.discount_percent}% off your first month! Redirecting to secure payment...` : "Referral code applied! Redirecting to secure payment...");
        } else if (plan === "student" && referralCode.trim()) {
          // User entered a code but it wasn't valid
          toast.warning("Referral code not found or invalid. Proceeding without referral...");
        } else {
          toast.success("Redirecting to secure payment...");
        }
        // Small delay to show the toast, then redirect
        setTimeout(() => {
          window.location.href = data.checkout_url;
        }, 500);
      } else {
        let errorMsg = "Failed to create checkout session";
        try {
          const error = await response.json();
          errorMsg = error.detail || errorMsg;
        } catch (e) {
          errorMsg = `Server error (${response.status})`;
        }
        console.error("Checkout error:", response.status, errorMsg);
        if (response.status === 400 && /already/i.test(errorMsg)) {
          // Guard caught a payment that had already gone through - no second checkout
          toast.success(errorMsg);
          fetchSubscription();
          if (setUser) {
            const me = await fetch(`${API}/auth/me`, { credentials: "include" });
            if (me.ok) setUser(await me.json());
          }
        } else {
          toast.error(errorMsg);
        }
        setLoading(null);
      }
    } catch (error) {
      console.error("Checkout network error:", error);
      toast.error(`Connection error: ${error.message || "Please check your internet connection"}`);
      setLoading(null);
    }
  };

  const currentTier = subscription?.subscription_tier;
  const isCurrentPlan = (plan) => currentTier === plan;

  const teacherButtonLabel = (key) => {
    if (isCurrentPlan(key)) return "Current Plan";
    if (key === "school") return "Apply for License";
    if (currentTier === "school") return "Upgrade Now";
    if (currentTier === "teacher_plus" && key === "teacher_pro") return "Upgrade Now";
    if (currentTier === "teacher_pro" && key === "teacher_plus") return "Switch Plan";
    return "Get Started";
  };

  // Full page loading overlay when redirecting to Stripe
  if (redirecting) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center space-y-4">
          <Loader2 className="w-12 h-12 animate-spin text-primary mx-auto" />
          <h2 className="text-xl font-semibold">Redirecting to Secure Payment</h2>
          <p className="text-muted-foreground">Please wait while we connect you to Stripe...</p>
        </div>
      </div>
    );
  }

  const activePlans = plans(audience === "teacher" ? TEACHER_PLANS : STUDENT_PLANS);

  const renderPlanButton = (key, plan) => {
    if (key === "free") {
      return (
        <Button
          className="w-full h-11"
          variant="outline"
          onClick={() => {
            if (user) {
              navigate("/");
            } else {
              setShowAuthModal(true);
            }
          }}
          data-testid="subscribe-btn-free"
        >
          {user ? "Current Plan" : "Start for Free"}
        </Button>
      );
    }

    // Starter is free but included for paid teachers
    const starterIncluded = key === "school" && ["teacher_plus", "teacher_pro"].includes(currentTier);
    const label = audience === "teacher"
      ? (starterIncluded ? "Included in Your Plan" : teacherButtonLabel(key))
      : (isCurrentPlan(key) ? "Current Plan" : "Get Started");

    return (
      <Button
        className="w-full h-11"
        variant={plan.popular ? "default" : "outline"}
        onClick={() => handleSubscribe(key)}
        disabled={!authChecked || loading === key || isCurrentPlan(key) || starterIncluded}
        data-testid={`subscribe-btn-${key}`}
      >
        {loading === key ? <Loader2 className="w-5 h-5 animate-spin" /> : label}
      </Button>
    );
  };

  return (
    <div className="min-h-screen">
      <div className="container max-w-5xl mx-auto px-4 py-12">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-4xl font-bold mb-4">Upgrade Your Coding Journey</h1>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
            {audience === "teacher"
              ? "Run live coding drills, track student progress, and manage your classroom — plans for every class size."
              : "Unlock more challenges, get hints when you're stuck, and create your own questions for others to solve."}
          </p>
        </div>

        {/* Audience Toggle */}
        <div className="flex justify-center mb-10">
          <div className="inline-flex rounded-full border border-border bg-card p-1" data-testid="plans-audience-toggle">
            <button
              onClick={() => setAudience("student")}
              className={`px-6 py-2.5 rounded-full text-sm font-semibold transition-colors ${
                audience === "student"
                  ? "bg-primary text-primary-foreground shadow"
                  : "text-muted-foreground hover:text-foreground"
              }`}
              data-testid="plans-toggle-student"
            >
              Student Plans
            </button>
            <button
              onClick={() => setAudience("teacher")}
              className={`px-6 py-2.5 rounded-full text-sm font-semibold transition-colors ${
                audience === "teacher"
                  ? "bg-primary text-primary-foreground shadow"
                  : "text-muted-foreground hover:text-foreground"
              }`}
              data-testid="plans-toggle-teacher"
            >
              Teacher Plans
            </button>
          </div>
        </div>

        {/* Status Check Banner */}
        {checkingStatus && (
          <div className="mb-8 p-4 rounded-xl bg-primary/10 border border-primary/20 flex items-center justify-center gap-3">
            <Loader2 className="w-5 h-5 animate-spin text-primary" />
            <span>Verifying your payment...</span>
          </div>
        )}

        {/* Current Subscription Banner */}
        {currentTier && currentTier !== "free" && (
          <div className="mb-8 p-4 rounded-xl bg-green-500/10 border border-green-500/20 flex items-center justify-center gap-3" data-testid="current-plan-banner">
            <CheckCircle className="w-5 h-5 text-green-500" />
            <span className="font-medium">
              You're on the {ALL_PLANS[currentTier]?.name || currentTier} plan!
            </span>
          </div>
        )}

        {/* School Membership Banner - Show for users who are part of school(s) but not paying subscribers */}
        {audience === "student" && subscription?.schools && subscription.schools.length > 0 && currentTier === "free" && (
          <div className="mb-8 p-6 rounded-2xl bg-gradient-to-br from-blue-500/10 via-cyan-500/10 to-teal-500/10 border border-blue-500/30" data-testid="school-membership-banner">
            <div className="flex items-start gap-4">
              <div className="p-3 rounded-xl bg-blue-500/20">
                <School className="w-6 h-6 text-blue-500" />
              </div>
              <div className="flex-1">
                <h3 className="text-lg font-bold text-foreground mb-1">
                  Congratulations! You're registered as part of {subscription.schools.length === 1 ? 'a school' : 'schools'}:
                </h3>
                <div className="mb-4">
                  {subscription.schools.map((school, index) => (
                    <span key={school.school_id} className="font-semibold text-blue-600 dark:text-blue-400">
                      {school.school_name}{index < subscription.schools.length - 1 ? ', ' : ''}
                    </span>
                  ))}
                </div>
                
                <p className="text-sm text-muted-foreground mb-4">
                  As a school member, you have access to these benefits:
                </p>
                
                <div className="grid sm:grid-cols-2 gap-2">
                  {["5 bonus challenges per day", "Show hints for all questions", "Create your own questions", "School leaderboard rankings"].map((benefit) => (
                    <div key={benefit} className="flex items-center gap-2 text-sm">
                      <div className="w-5 h-5 rounded-full bg-green-500/20 flex items-center justify-center shrink-0">
                        <Check className="w-3 h-3 text-green-500" />
                      </div>
                      <span>{benefit}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Referral Code Section - student tab only, not subscribed and not a school member */}
        {audience === "student" && (!currentTier || currentTier === "free") && 
         (!subscription?.schools || subscription.schools.length === 0) && (
          <div className="mb-8 max-w-md mx-auto">
            <button
              onClick={() => setShowReferralInput(!showReferralInput)}
              className="w-full flex items-center justify-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors py-2"
              data-testid="toggle-referral-input"
            >
              <Gift className="w-4 h-4" />
              {showReferralInput ? "Hide referral code" : "Have a referral code? Get 50% off your first month"}
              {showReferralInput ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
            </button>
            
            {showReferralInput && (
              <div className="mt-3 p-4 rounded-xl border border-border bg-card/50" data-testid="referral-code-panel">
                <label className="block text-sm font-medium mb-2">
                  Enter referral code
                </label>
                <div className="flex gap-2">
                  <Input
                    type="text"
                    placeholder="e.g., REF-7X9K2M"
                    value={referralCode}
                    onChange={(e) => setReferralCode(e.target.value.toUpperCase())}
                    className="flex-1 uppercase"
                    maxLength={10}
                    data-testid="referral-code-input"
                  />
                </div>
                {referralCheck.status === "checking" && (
                  <p className="mt-2 text-xs text-muted-foreground flex items-center gap-1" data-testid="referral-check-checking"><Loader2 className="w-3 h-3 animate-spin" />Checking code...</p>
                )}
                {referralValid && (
                  <p className="mt-2 text-xs text-green-600 dark:text-green-400 flex items-center gap-1" data-testid="referral-check-valid">
                    <CheckCircle className="w-3.5 h-3.5" />Code from {referralCheck.referrerName} - you'll get {referralCheck.percent}% off your first month.
                  </p>
                )}
                {referralCheck.status === "invalid" && (
                  <p className="mt-2 text-xs text-red-600 dark:text-red-400 flex items-center gap-1" data-testid="referral-check-invalid">
                    <XCircle className="w-3.5 h-3.5" />That code isn't valid (codes belong to current Student subscribers and can't be your own).
                  </p>
                )}
                <div className="mt-3 pt-3 border-t border-border/60 text-xs text-muted-foreground space-y-1" data-testid="referral-explainer">
                  <p className="font-medium text-foreground">How referral rewards work</p>
                  <p><span className="font-medium text-foreground">You</span> get 50% off your first month of the Student Plan, applied automatically at checkout.</p>
                  <p><span className="font-medium text-foreground">Your friend</span> gets 50% off their next month once your first payment goes through.</p>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Pricing Cards */}
        <div className={`grid gap-6 mx-auto ${audience === "teacher" ? "md:grid-cols-3 max-w-5xl" : "md:grid-cols-2 max-w-3xl"}`}>
          {Object.entries(activePlans).map(([key, plan]) => (
            <div
              key={key}
              className={`relative rounded-2xl border-2 p-6 transition-all ${
                plan.popular
                  ? "border-primary bg-primary/5 shadow-lg shadow-primary/10 md:scale-105"
                  : "border-border bg-card hover:border-primary/50"
              }`}
              data-testid={`plan-card-${key}`}
            >
              {plan.popular && (
                <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                  <span className="px-3 py-1 text-xs font-semibold bg-primary text-primary-foreground rounded-full">
                    Most Popular
                  </span>
                </div>
              )}

              <div className="flex items-center gap-3 mb-4">
                <div className={`p-2.5 rounded-xl ${plan.popular ? "bg-primary/20" : "bg-muted"}`}>
                  <plan.icon className={`w-5 h-5 ${plan.popular ? "text-primary" : "text-muted-foreground"}`} />
                </div>
                <div>
                  <h3 className="font-bold text-lg">{plan.name}</h3>
                </div>
              </div>

              <div className="mb-4">
                {plan.price === 0 ? (
                  <span className="text-3xl font-bold">Free</span>
                ) : key === "student" && referralValid ? (
                  <div data-testid="student-referral-price">
                    <span className="text-lg text-muted-foreground line-through mr-2">${plan.price}</span>
                    <span className="text-3xl font-bold">${plan.price / 2}</span>
                    <span className="text-muted-foreground text-sm ml-1">first {plan.period}</span>
                    <p className="text-xs text-muted-foreground mt-1">then ${plan.price}/{plan.period} - referral discount applied at checkout</p>
                  </div>
                ) : (
                  <>
                    <span className="text-3xl font-bold">${plan.price}</span>
                    <span className="text-muted-foreground text-sm ml-1">/{plan.period}</span>
                  </>
                )}
              </div>

              <ul className="space-y-2 mb-6">
                {plan.features.map((feature) => (
                  <li key={feature} className="flex items-start gap-2">
                    <Check className="w-4 h-4 text-green-500 shrink-0 mt-0.5" />
                    <span className="text-sm">{feature}</span>
                  </li>
                ))}
              </ul>

              {renderPlanButton(key, plan)}
            </div>
          ))}
        </div>

        {/* Info Note */}
        <div className="mt-12 text-center">
          {audience === "teacher" ? (
            <p className="text-sm text-muted-foreground flex items-center justify-center gap-2">
              <Sparkles className="w-4 h-4 text-primary" />
              Paid teacher plans activate instantly — no application or approval needed.
            </p>
          ) : (
            <p className="text-sm text-muted-foreground">
              All plans include access to daily warm-up questions and leaderboards.
            </p>
          )}
        </div>
      </div>

      {/* Auth Modal */}
      <AuthModal
        isOpen={showAuthModal}
        onClose={() => setShowAuthModal(false)}
        onSuccess={(userData) => {
          setShowAuthModal(false);
          if (setUser) setUser(userData);
          toast.success("Welcome! You're now signed in — pick a plan to continue.");
        }}
        initialMode="register"
      />
    </div>
  );
}
